import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { QuoteConfirmationReview as Review } from './quoteReviewContract';
import type { ConfirmationRetry } from '@/stores/assistantStore';
import { QuoteReceiptTimeField } from './QuoteReceiptTimeField';

function noReceiptListener(): void {}

export function QuoteConfirmationReview({
  review,
  onReceiptChange,
  retry,
}: {
  review: Review | { action: 'serviceLine'; quote: Review['quote'] };
  /**
   * Acceptance only (#2821): the receipt time the staff member confirms on
   * this card, or null while it is not valid. Confirm sends only this value.
   */
  onReceiptChange?: (receivedAt: string | null) => void;
  /** The last confirm was refused with its plan kept: restore its time and show why. */
  retry?: ConfirmationRetry | undefined;
}) {
  const { t, i18n } = useTranslation('assistant');
  const { t: tc } = useTranslation('common');
  const { t: tw } = useTranslation('workorders');
  const q = review.quote;
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const number = (value: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 8 }).format(value);
  // Net unit prices retain four decimal places in cents: six in euros.
  const money = (cents: number, unit = false) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: q.pricing.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: unit ? 6 : 2,
    }).format(cents / 100);
  const instant = (value: string | null) =>
    value === null
      ? t('quoteReview.none')
      : new Intl.DateTimeFormat(locale, {
          timeZone: q.workshopTimezone,
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(new Date(value));
  const preciseInstant = (value: string | null) =>
    value === null ? t('quoteReview.none') : `${instant(value)} · ${value}`;
  // Same mapping as GroupedInvoiceLines; an unmapped unit renders as sent (UnitSelect).
  const unitLabel = (unit: string) => tc(`units.${unit}`, { defaultValue: unit });
  // Match buildInvoiceLineValues in staff-api billing-service: invalid quantities bill one unit.
  const billedExtraQuantity = (quantity: number | null) =>
    quantity !== null && Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  // The server names the surcharge line in English; its label is ours to translate.
  const lineDescription = (line: Review['quote']['pricing']['lines'][number]) =>
    line.lineType === 'surcharge' ? tw('detail.smallSuppliesSurcharge') : line.description;
  const yesNo = (value: boolean) => t(value ? 'quoteReview.yes' : 'quoteReview.no');
  const resource = (label: { name: string } | null, id: string | null) =>
    label?.name ?? id ?? t('quoteReview.none');
  const partyRole = (role: string) =>
    ['orderer', 'payer', 'primary_contact', 'owner', 'holder', 'driver'].includes(role)
      ? t(`quoteReview.partyRoles.${role}`)
      : role;
  const itemStatus = (status: string) =>
    ['pending', 'approved', 'declined', 'completed'].includes(status)
      ? t(`quoteReview.itemStatuses.${status}`)
      : status;
  const field = (key: string, value: string) => (
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
      <strong>{t(`quoteReview.${key}`)}: </strong>
      {value}
    </Typography>
  );

  return (
    <Box data-testid="quote-confirmation-review" sx={{ mb: 2 }}>
      <Typography variant="subtitle2">
        {q.workOrderNumber} · {q.vehicle.registrationNumber}
      </Typography>
      {field('customer', q.orderer?.displayName ?? t('quoteReview.none'))}
      {q.parties.map((party) => (
        <Typography variant="body2" key={party.id}>
          {partyRole(party.role)}: {party.displayName}
        </Typography>
      ))}
      {field('expires', instant(q.quoteExpiresAt))}
      {field('timezone', q.workshopTimezone)}
      {q.customerNotes && field('notes', q.customerNotes)}

      <TableContainer sx={{ my: 1 }}>
        <Table size="small" aria-label={t('quoteReview.prices')}>
          <TableHead>
            <TableRow>
              {['description', 'quantity', 'unitNet', 'discount', 'vat', 'lineNet'].map((key) => (
                <TableCell key={key}>{t(`quoteReview.${key}`)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {q.pricing.lines.map((line, index) => (
              <TableRow key={`${line.sortOrder}-${index}`}>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                  {[line.groupName, lineDescription(line), line.groupNotes].filter(Boolean).join(' · ')}
                </TableCell>
                <TableCell>
                  {number(line.quantity)} {unitLabel(line.unit || 'piece')}
                </TableCell>
                <TableCell>{money(line.unitPriceCents, true)}</TableCell>
                <TableCell>{number(line.discountPercent)}%</TableCell>
                <TableCell>
                  {line.taxable
                    ? `${number(line.taxPercent ?? q.pricing.defaultVatPercent)}%`
                    : t('quoteReview.nonTaxable')}
                </TableCell>
                <TableCell>{money(line.lineTotalCents)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {field('net', money(q.pricing.netSubtotalCents))}
      {field('tax', money(q.pricing.taxCents))}
      {field('gross', money(q.pricing.grossTotalCents))}

      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        {t('quoteReview.scope')}
      </Typography>
      <Typography variant="body2">{t('quoteReview.scopeExplanation')}</Typography>
      {q.services.map((service) => (
        <Box
          key={service.bookingServiceId}
          sx={{ mt: 1, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}
        >
          <Typography variant="body2" fontWeight={600}>
            {service.name}
          </Typography>
          {field(
            'plannedHours',
            service.plannedHours === null
              ? t('quoteReview.none')
              : `${number(service.plannedHours)} h`
          )}
          {field('serviceScheduledAt', preciseInstant(service.scheduledAt))}
          {field('mechanic', resource(service.assignedMechanic, service.assignedMechanicId))}
          {field('lift', resource(service.assignedLift, service.assignedLiftId))}
          {service.notes && field('notes', service.notes)}
          {service.laborItems.map((line) => (
            <Typography variant="body2" key={line.id}>
              {line.description} · {number(line.estimatedHours)} h · {t('quoteReview.discount')}{' '}
              {number(line.discountPercent)}%
            </Typography>
          ))}
          {service.parts.map((part) => (
            <Typography variant="body2" key={part.id} sx={{ whiteSpace: 'pre-wrap' }}>
              {part.name} · {number(part.quantity)} × {money(part.unitPriceCents, true)}{' '}
              {t('quoteReview.netLabel')} · {t('quoteReview.discount')}{' '}
              {number(part.discountPercent)}% · {t('quoteReview.vat')} {number(part.vatPercent)}%
              {part.notes ? ` · ${part.notes}` : ''}
            </Typography>
          ))}
          {service.extras.map((extra) => (
            <Typography variant="body2" key={extra.id}>
              {extra.description} ·{' '}
              {extra.hasVariableQuantity
                ? `${number(billedExtraQuantity(extra.quantity))} ${extra.unit ? unitLabel(extra.unit) : ''} × `
                : ''}
              {money(extra.amountCents, true)} {t('quoteReview.netLabel')} ·{' '}
              {extra.isTaxable
                ? `${t('quoteReview.vat')} ${number(extra.vatPercent)}%`
                : t('quoteReview.nonTaxable')}
            </Typography>
          ))}
        </Box>
      ))}
      {q.legacyItems.map((item) => (
        <Typography variant="body2" key={item.id}>
          {item.description} · {itemStatus(item.status)} · {t('quoteReview.labor')}{' '}
          {money(item.laborCostCents, true)} · {t('quoteReview.parts')}{' '}
          {money(item.partsCostCents, true)} {t('quoteReview.netLabel')}
        </Typography>
      ))}
      {q.legacyParts.map((part) => (
        <Typography variant="body2" key={part.id}>
          {part.partName} · {number(part.quantity)} {unitLabel(part.unit || 'piece')} ×{' '}
          {money(part.unitSellPriceCents, true)} {t('quoteReview.netLabel')} ·{' '}
          {t('quoteReview.discount')} {number(part.discountPercent)}%
        </Typography>
      ))}

      {review.action === 'accept' ? (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {t('quoteReview.schedule')}
          </Typography>
          {field(
            'arrival',
            review.acceptance.schedulingType === 'floating'
              ? `${review.acceptance.scheduledAt.slice(0, 10)} · ${t('quoteReview.floating')}`
              : `${instant(review.acceptance.scheduledAt)} · ${review.acceptance.scheduledAt}`
          )}
          {field('pickup', preciseInstant(review.acceptance.customerRequestedPickupAt))}
          {field(
            'serviceScheduling',
            q.schedule.serviceScheduling === null
              ? t('quoteReview.none')
              : ['sequential', 'parallel'].includes(q.schedule.serviceScheduling)
                ? t(`quoteReview.serviceSchedulingModes.${q.schedule.serviceScheduling}`)
                : q.schedule.serviceScheduling
          )}
          {field(
            'plannedHours',
            review.acceptance.estimatedDurationHours === null
              ? t('quoteReview.none')
              : `${number(review.acceptance.estimatedDurationHours)} h`
          )}
          {field(
            'mechanic',
            resource(q.resources.assignedMechanic, q.resources.assignedMechanicId)
          )}
          {field('lift', resource(q.resources.assignedLift, q.resources.assignedLiftId))}
          <Typography variant="body2">{t('quoteReview.resourcesRetained')}</Typography>
          {field('customerWaits', yesNo(review.acceptance.customerWaits))}
          {field('minorMaintenance', yesNo(review.acceptance.allowMinorMaintenance))}
          {field('jointInspection', yesNo(review.acceptance.jointInspectionRequested))}
          {field(
            'additionalLimit',
            review.acceptance.preapprovedAdditionalWorkLimitCents === null
              ? t('quoteReview.none')
              : money(review.acceptance.preapprovedAdditionalWorkLimitCents)
          )}
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {t('quoteReview.attestation')}
          </Typography>
          <Typography variant="body2">{t('quoteReview.attestationExplanation')}</Typography>
          {field('customer', review.customerApproval.customerName)}
          <QuoteReceiptTimeField
            key={`${retry?.locked ? 'locked' : 'open'}:${retry?.approvalReceivedAt ?? 'prefill'}`}
            prefill={review.receipt}
            timeZone={q.workshopTimezone}
            onChange={onReceiptChange ?? noReceiptListener}
            initialReceivedAt={retry?.approvalReceivedAt}
            locked={retry?.locked === true}
            serverError={retry && retry.reason !== 'retryable' ? retry.reason : undefined}
          />
          {field('channel', t(`quoteReview.channels.${review.customerApproval.channel}`))}
          {field('statement', review.customerApproval.statement)}
          {review.customerApproval.reference &&
            field('reference', review.customerApproval.reference)}
        </>
      ) : review.action === 'reject' ? (
        field('rejectionReason', review.reason)
      ) : null}
      <Typography variant="body2" sx={{ mt: 1 }}>
        {t('quoteReview.noDelivery')}
      </Typography>
    </Box>
  );
}
